import {referenceID} from '@github-ui/copilot-chat-helpers/copilot-chat-helpers'
import type {CopilotChatCommandType} from '@github-ui/copilot-chat-types'
import {testIdProps} from '@github-ui/test-id-props'
import {useEffect, useMemo, useRef} from 'react'
import {unified} from 'unified'

import {useChatStateValues} from '../utils/CopilotChatContext'
import {useChatManager} from '../utils/CopilotChatManagerContext'
import {userMessageParser} from '../utils/user-message/user-message-parser'
import {getAllMentionedReferenceIds} from '../utils/user-message/user-message-utils'
import styles from './ChatInput.module.css'
import userMessageStyles from './UserMessage.module.css'

interface ChatInputPreviewProps {
  text: string
  availableCommandKeys: CopilotChatCommandType[]
  ref?: React.Ref<HTMLDivElement>
}

/**
 * This is displayed over the <textarea> for the ChatInput and is used to display rich text e.g. to highlight mentions
 * and references.
 */
export function ChatInputPreview({ref, text, availableCommandKeys}: ChatInputPreviewProps) {
  const {currentReferences} = useChatStateValues('currentReferences')
  const manager = useChatManager()

  // Memoized so the markdown parse only runs when its inputs change, not on every
  // re-render of the input (which happens on each keystroke while typing).
  const messageTree = useMemo(
    () =>
      unified()
        .use(userMessageParser, {
          references: currentReferences,
          commands: availableCommandKeys,
          rawFences: true,
        })
        .parse(text),
    [text, currentReferences, availableCommandKeys],
  )

  const mentionedReferences = useMemo(() => getAllMentionedReferenceIds(messageTree), [messageTree])

  const previouslyMentionedReferences = useRef(mentionedReferences)
  useEffect(function removeReferencesForDeletedMentions() {
    for (const id of previouslyMentionedReferences.current)
      if (!mentionedReferences.has(id)) {
        const toRemove = currentReferences.find(r => referenceID(r) === id)
        if (toRemove) manager.removeReference(toRemove)
      }
    previouslyMentionedReferences.current = mentionedReferences
  })

  return (
    <div
      id="copilot-chat-textarea-preview"
      {...testIdProps('copilot-chat-input-textarea-preview')}
      aria-hidden
      className={styles.inputPreview}
      ref={ref}
      role="presentation"
    >
      {messageTree.children.map((node, i) => {
        switch (node.type) {
          case 'text':
            return node.value
          case 'command':
            return (
              <span className={userMessageStyles.command} key={i} {...testIdProps('input-preview-command')}>
                {node.value}
              </span>
            )
          case 'reference-mention':
            return node.data.mentionedReferenceId !== undefined ? (
              <span className={userMessageStyles.mention} key={i} {...testIdProps('input-preview-ref')}>
                {node.value}
              </span>
            ) : (
              node.value
            )
          case 'code-block':
            return node.value
        }
      })}
    </div>
  )
}
